import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { db } from "../../database/firebase";
import { motion } from "framer-motion";
import {
  Plus,
  History,
  Calendar as CalendarIcon,
} from "lucide-react";
import toast from "react-hot-toast";
import {
  updateMonthlySummary,
  updateOverallHistory,
  getCurrentMonth,
  getMonthFromDate
} from "../../utils/wellnessHelpers";

const DailyTracker = () => {
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);
  
  // State
  const [todayData, setTodayData] = useState(null);
  const [goals, setGoals] = useState({
    water: { daily: 8 },
    bathroomVisits: { daily: 7 }
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Derived state
  const drinksCount = todayData?.waterGlasses || 0;
  const bathroomVisits = todayData?.bathroomVisits || 0;
  const drinksGoal = goals.water.daily;
  const visitsGoal = goals.bathroomVisits.daily;
  
  // Calculations
  const mlPerCup = 250;
  const currentMl = drinksCount * mlPerCup;
  const goalMl = drinksGoal * mlPerCup;
  const drinksPercentage = Math.round((drinksCount / drinksGoal) * 100);
  const visitsPercentage = Math.round((bathroomVisits / visitsGoal) * 100);

  // Load data on mount
  useEffect(() => {
    if (user?.uid) {
      loadTrackerData();
    }
  }, [user]);

  /**
   * Load today's tracking data from Firestore
   */
  const loadTrackerData = async () => {
    try {
      setLoading(true);
      const userDoc = await getDoc(doc(db, "users", user.uid));
      
      if (userDoc.exists()) {
        const data = userDoc.data();
        const today = new Date().toISOString().split("T")[0];
        
        // Find today's record
        const todayRecord = data.healthInfo?.dailyTracking?.records?.find(
          (r) => r.date === today
        );
        
        // Set today's data or create empty record
        setTodayData(todayRecord || {
          date: today,
          waterGlasses: 0,
          waterMl: 0,
          bathroomVisits: 0,
          createdAt: new Date().toISOString()
        });
        
        // Load goals
        if (data.healthInfo?.goals) {
          setGoals(prev => ({
            ...prev,
            ...data.healthInfo.goals
          }));
        }
      }
    } catch (error) {
      console.error("Error loading tracker data:", error);
      toast.error("Failed to load tracker data");
    } finally {
      setLoading(false);
    }
  };

  /**
   * Save data to Firestore with monthly and historical updates
   */
  const saveToFirestore = async (updates) => {
    if (!user?.uid) return;

    try {
      setSaving(true);
      const today = new Date().toISOString().split("T")[0];
      const currentMonth = getCurrentMonth();
      const userRef = doc(db, "users", user.uid);
      const userDoc = await getDoc(userRef);
      const userData = userDoc.data();

      const existingRecords = userData.healthInfo?.dailyTracking?.records || [];
      const todayIndex = existingRecords.findIndex((r) => r.date === today);

      let updatedRecords;
      if (todayIndex >= 0) {
        // Update existing record
        updatedRecords = [...existingRecords];
        updatedRecords[todayIndex] = {
          ...updatedRecords[todayIndex],
          ...updates,
          updatedAt: new Date().toISOString(),
        };
      } else {
        // Create new record
        updatedRecords = [
          ...existingRecords,
          {
            date: today,
            waterGlasses: 0,
            waterMl: 0,
            bathroomVisits: 0,
            calories: { intake: 0, burned: 0 },
            macros: { protein: 0, carbs: 0, fat: 0 },
            meals: [],
            steps: 0,
            sleep: 0,
            ...updates,
            createdAt: new Date().toISOString(),
          },
        ];
      }

      // Calculate monthly summary
      const monthlySummary = updateMonthlySummary(currentMonth, updatedRecords, goals);
      
      // Calculate overall history
      const overallHistory = updateOverallHistory(updatedRecords);

      // Prepare update object
      const updateObject = {
        "healthInfo.dailyTracking.records": updatedRecords,
        "healthInfo.dailyTracking.lastTrackedDate": today,
        updatedAt: new Date().toISOString(),
      };

      // Add monthly summary
      if (monthlySummary) {
        updateObject[`healthInfo.monthlySummaries.${currentMonth}`] = monthlySummary;
      }

      // Add overall history
      updateObject["healthInfo.overallHistory"] = overallHistory;

      await updateDoc(userRef, updateObject);

      // Update local state
      setTodayData(prev => ({
        ...prev,
        ...updates,
        updatedAt: new Date().toISOString()
      }));

      return true;
    } catch (error) {
      console.error("Error saving to Firestore:", error);
      toast.error("Failed to save data");
      return false;
    } finally {
      setSaving(false);
    }
  };

  /**
   * Add a drink
   */
  const handleAddDrink = async () => {
    const newCount = Math.min(drinksCount + 1, drinksGoal);
    const newMl = newCount * mlPerCup;
    
    // Optimistic update
    setTodayData(prev => ({
      ...prev,
      waterGlasses: newCount,
      waterMl: newMl
    }));

    const success = await saveToFirestore({
      waterGlasses: newCount,
      waterMl: newMl
    });

    if (success) {
      toast.success("Drink added!");
    } else {
      // Revert on error
      setTodayData(prev => ({
        ...prev,
        waterGlasses: drinksCount,
        waterMl: currentMl
      }));
    }
  };

  /**
   * Add a bathroom visit
   */
  const handleAddVisit = async () => {
    const newCount = Math.min(bathroomVisits + 1, visitsGoal);
    
    // Optimistic update
    setTodayData(prev => ({
      ...prev,
      bathroomVisits: newCount
    }));

    const success = await saveToFirestore({
      bathroomVisits: newCount
    });

    if (success) {
      toast.success("Visit logged!");
    } else {
      // Revert on error
      setTodayData(prev => ({
        ...prev,
        bathroomVisits: bathroomVisits
      }));
    }
  };

  /**
   * Get pattern analysis
   */
  const getPattern = () => {
    if (bathroomVisits >= 6 && bathroomVisits <= 8) {
      return "Healthy pattern";
    } else if (bathroomVisits < 4) {
      return "Low frequency";
    } else if (bathroomVisits > 10) {
      return "High frequency";
    }
    return "Normal pattern";
  };

  /**
   * Get today's date formatted
   */
  const getTodayDate = () => {
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const today = new Date();
    return `${days[today.getDay()]} (today)`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center pb-24">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-500 mx-auto mb-4"></div>
          <p className="text-gray-500">Loading tracker...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-24"> 
      {/* Content */}
      <div className="px-6 py-6 space-y-4">
        {/* Header Info */}
        <div className="mb-2">
          <h1 className="text-2xl font-bold text-gray-900">Daily Tracker</h1>
          <p className="text-sm text-gray-500">{getTodayDate()}</p>
        </div>

        {/* Drinks Intake Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-gradient-to-br from-cyan-50 to-blue-100 rounded-3xl p-6 relative overflow-hidden"
        >
          {/* Background decoration */}
          <div className="absolute top-0 right-0 w-32 h-32 bg-white/20 rounded-full -mr-16 -mt-16"></div>
          
          <div className="relative">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-white rounded-2xl flex items-center justify-center">
                  <span className="text-2xl">🥤</span>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900">Drinks Intake</h3>
                  <p className="text-sm text-gray-600">
                    {drinksCount} cups | {drinksGoal} cups planned
                  </p>
                </div>
              </div>
              <div className="bg-cyan-600 text-white text-xs font-bold px-3 py-1 rounded-full">
                {drinksPercentage}%
              </div>
            </div>

            {/* Progress Info */}
            <div className="mb-3">
              <p className="text-sm text-gray-700 font-medium">
                {currentMl} ml up to {goalMl} ml planned
              </p>
            </div>

            {/* Progress Bar */}
            <div className="h-3 bg-white/50 rounded-full overflow-hidden mb-4">
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${Math.min(drinksPercentage, 100)}%` }}
                transition={{ duration: 0.5 }}
                className="h-full bg-cyan-500 rounded-full"
              />
            </div>

            {/* Add Button */}
            <button
              onClick={handleAddDrink}
              disabled={saving || drinksCount >= drinksGoal}
              className="w-full bg-white hover:bg-gray-50 text-gray-900 font-semibold py-3 rounded-2xl flex items-center justify-center gap-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Plus className="w-5 h-5" />
              {saving ? "Saving..." : "Drinks"}
            </button>
          </div>
        </motion.div>

        {/* Bathroom Visits Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-3xl p-6 relative overflow-hidden"
        >
          {/* Background decoration */}
          <div className="absolute bottom-0 left-0 w-32 h-32 bg-white/20 rounded-full -ml-16 -mb-16"></div>
          
          <div className="relative">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-white rounded-2xl flex items-center justify-center">
                  <span className="text-2xl">🚽</span>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900">Bathroom Visits</h3>
                  <p className="text-2xl font-bold text-gray-900">
                    {bathroomVisits} <span className="text-base text-gray-600">/ {visitsGoal} visits</span>
                  </p>
                </div>
              </div>
            </div>

            {/* Pattern Info */}
            <div className="mb-4">
              <p className="text-sm text-gray-700">
                Pattern: <span className="font-semibold">{getPattern()}</span>
              </p>
            </div>

            {/* Progress Dots */}
            <div className="flex items-center gap-2 mb-4">
              {Array.from({ length: visitsGoal }).map((_, i) => (
                <div
                  key={i}
                  className={`flex-1 h-2 rounded-full transition-all ${
                    i < bathroomVisits
                      ? "bg-orange-400"
                      : "bg-white/50"
                  }`}
                />
              ))}
            </div>

            {/* Add Button */}
            <button
              onClick={handleAddVisit}
              disabled={saving || bathroomVisits >= visitsGoal}
              className="w-full bg-white hover:bg-gray-50 text-gray-900 font-semibold py-3 rounded-2xl flex items-center justify-center gap-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Plus className="w-5 h-5" />
              {saving ? "Saving..." : "Visits"}
            </button>
          </div>
        </motion.div>

        {/* Quick Stats */}
        <div className="grid grid-cols-2 gap-3">
          <div className="bg-white rounded-2xl p-4">
            <div className="flex items-center gap-2 mb-2">
              <History className="w-5 h-5 text-purple-500" />
              <span className="text-sm text-gray-600">History</span>
            </div>
            <p className="text-2xl font-bold text-gray-900">{drinksPercentage}%</p>
            <p className="text-xs text-gray-500">water goal</p>
          </div>
          <div className="bg-white rounded-2xl p-4">
            <div className="flex items-center gap-2 mb-2">
              <CalendarIcon className="w-5 h-5 text-blue-500" />
              <span className="text-sm text-gray-600">Today</span>
            </div>
            <p className="text-2xl font-bold text-gray-900">{visitsPercentage}%</p>
            <p className="text-xs text-gray-500">visits goal</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DailyTracker;
